import { all, call, put, select, takeLatest } from 'redux-saga/effects';
import mixpanel from 'mixpanel-browser';

import {
  attemptPrivateToken,
  attemptTemporaryToken,
  bannerVisibleChanged,
  checkIfHasPrimaryVet,
  customerInformationFailed,
  customerInformationLoaded,
  discountDataLoaded,
  getAllBrandsData,
  getDiscountInfo as GET_DISCOUNT_INFO,
  getDiscountInfo,
  loadDataForPrivateToken,
  loadMultiModalData,
  logIn,
  logOut,
  privateTokenCompleted,
  privateTokenFailed,
  saveImpersonateCustomerId,
  saveUserInfo,
  saveUserTermsOfService,
  sessionError,
  setPrivateToken,
  setTemporaryToken,
  showGenericError,
  startupFailed,
  startupFinished,
  temporaryTokenCompleted,
  temporaryTokenFailed,
  termsOfServiceAccepted,
} from '../actions';
import { POLICY_STATUS } from '../constants';
import {
  apiCallWithApiKey,
  get,
  getData,
  post,
  removeData,
  saveData,
  saveDataOnSession,
  X_API_KEY_ADMIN,
} from '../services/api';
import {
  API_TOKEN,
  BANNER_PREFERENCES_KEY,
  IMPERSONATE_EMAIL,
  IMPERSONATE_KEY,
  USER_COMPANY_KEY,
  USER_INFO_KEY,
  WEBVIEW_API_TOKEN,
} from '../services/constants';

import {
  delay,
  getUniqueCompanies,
  maskPhoneNumber,
} from '../services/utils';
import { getMultibrandPolicies, getPolicySummary } from './policies';
import {
  useCustomerV1T176413ForSagas,
  usePetCloudCustomerV1T174565ForSagas,
} from '../services/featureFlagsForSagas';
import { MIXPANEL_LOGIN_EVENTS } from '../services/mixpanel';
import { getApplicationMessages } from './common';
import { doPetLoad } from './settings';

export function* loadCachedUserInfo() {
  const data = yield call(getData, USER_INFO_KEY);
  const userInfo = JSON.parse(data);

  if (userInfo) {
    yield put(saveUserInfo({
      ...userInfo,
      OtherPhoneNumber: userInfo.OtherPhoneNumber
        && maskPhoneNumber(userInfo.OtherPhoneNumber),
      PhoneNumber: maskPhoneNumber(userInfo.PhoneNumber),
    }));
  }
}

function* removeAllData() {
  yield call(removeData, USER_INFO_KEY);
  yield call(removeData, API_TOKEN);
  yield call(removeData, IMPERSONATE_KEY);
  yield call(removeData, BANNER_PREFERENCES_KEY);
  yield call(removeData, WEBVIEW_API_TOKEN);
}

export function* syncUserInformationStoreWithRedux() {
  const store = yield select(({ accountLogin, personalInformation }) => ({
    accountLogin,
    personalInformation,
  }));

  if (store.accountLogin.userInfo && store.personalInformation.userInfo) {
    const userInfo = {
      ...store.accountLogin.userInfo,
      ...store.personalInformation.userInfo,
    };

    yield call(saveData, USER_INFO_KEY, JSON.stringify(userInfo));
  }
}

export async function createLogAuth({
  dataDogTag,
  error,
  httpStatus,
  mixpanelKey,
  email,
  useCustomerAPIV1,
}) {
  // 'custom' log
  if (mixpanelKey) {
    mixpanel.track(mixpanelKey, { email, error });
  }

  if (dataDogTag) {
    const service = useCustomerAPIV1 ? 'customer' : 'api';
    const urlLog = `${service}/Log/LoginError`;
    const userAgent = navigator.userAgent;
    const formattedError = `${dataDogTag} - ${error}`;

    const body = {
      Email: email,
      Error: formattedError,
      HttpStatusCode: httpStatus,
      OperatingSystem: 'petcloud-web',
      UserAgent: userAgent,
    };

    // avoid duplicated logs validating custom log...
    if (!mixpanelKey) {
      mixpanel.track(MIXPANEL_LOGIN_EVENTS.error, {
        email,
        error: formattedError,
      });
    }

    const headers = useCustomerAPIV1 ? X_API_KEY_ADMIN : {};
    await apiCallWithApiKey(urlLog, { body, headers, method: 'POST' });
  }
}

export function* getDiscountData(dispatch, { payload }) {
  const url = `customer/DiscountCard/Customer/${payload}`;
  const discountData = yield call(get, dispatch, url);
  if (discountData.success) {
    yield put(discountDataLoaded(discountData));
  }
}

export function* getUserInformation(dispatch) {
  const token = yield call(getData, 'token');
  let response = {};

  if (token) {
    const urlService = 'customer/Account/personalInfo';
    response = yield call(get, dispatch, urlService);

    if (response.success && response.Data) {
      const companies = response.Data.Companies || [];
      const uniqueCompanies = getUniqueCompanies(companies);

      const personalInfoNullSafe = {
        ...response.Data,
        Address: {
          ...response.Data.Address,
          Line1: response.Data.Address.Line1 || '',
          Line2: response.Data.Address.Line2 || '',
          Phone1: response.Data.Address.Phone1 || '',
          Phone2: response.Data.Address.Phone2 || '',
        },
        Companies: uniqueCompanies,
        OtherPhoneNumber: response.Data.OtherPhoneNumber || '',
        PhoneNumber: response.Data.PhoneNumber || '',
      };

      const personalInformation = {
        ...personalInfoNullSafe,
        OtherPhoneNumber: personalInfoNullSafe.OtherPhoneNumber
          && maskPhoneNumber(personalInfoNullSafe.OtherPhoneNumber),
        PhoneNumber: personalInfoNullSafe.PhoneNumber
          && maskPhoneNumber(personalInfoNullSafe.PhoneNumber),
      };

      yield put(saveUserInfo(personalInformation));
      yield call(saveData, USER_INFO_KEY, JSON.stringify(personalInfoNullSafe));
      yield call(saveData, USER_COMPANY_KEY,
        JSON.stringify(personalInfoNullSafe.Company));
      yield put(checkIfHasPrimaryVet());
      yield put(getDiscountInfo(personalInformation.CustomerId));

      const impersonateEmail = getData(IMPERSONATE_EMAIL);

      if (impersonateEmail) {
        yield put(saveImpersonateCustomerId({
          adminEmail: impersonateEmail,
          customerId: personalInformation.CustomerGuid || '',
        }));
      }
    } else {
      const useCustomerAPIV1 = yield useCustomerV1T176413ForSagas();
      yield call(createLogAuth, {
        dataDogTag: 'personalInfo',
        error: response?.Message,
        httpStatus: response?.status || response?.statusCode,
        mixpanelKey: MIXPANEL_LOGIN_EVENTS.getPersonalInfoError,
        useCustomerAPIV1,
      });
    }
  }

  yield put(startupFinished());

  return response;
}

export function* getUserTermsOfService(dispatch) {
  const token = yield call(getData, 'token');
  let response = {};

  if (token) {
    response = yield call(get, dispatch, 'api/User/UserHasTermOfService');
    if (response.success && response.Data) {
      yield put(saveUserTermsOfService(response.Data));
    }
  }

  return response;
}

export function* loadCachedBannerVisible() {
  const data = yield call(getData, BANNER_PREFERENCES_KEY);
  const { visible } = JSON.parse(data) || { visible: true };
  yield put(bannerVisibleChanged({ visible: !!visible }));
}

export function* startUpApp(dispatch) {
  const token = yield call(getData, 'token');

  if (token) {
    yield put(setPrivateToken({ privateToken: token }));
    yield call(loadCachedBannerVisible);
    yield call(getUserTermsOfService, dispatch);
    yield call(getPolicySummary, dispatch);
    const personalInfo = yield call(getUserInformation, dispatch);

    if (!personalInfo?.success || !personalInfo?.Data) {
      yield put(startupFailed());
      return;
    }

    yield call(doPetLoad, dispatch);
    yield call(getApplicationMessages, dispatch);
    yield call(getMultibrandPolicies, dispatch);
  } else {
    yield put(startupFailed());
  }
}

export function* doLogout(dispatch) {
  yield call(post, dispatch, 'api/Authentication/LogOut');
  yield put(logOut());
  yield call(removeAllData);
}

function* handleB2CToken({
  accessToken = '',
  dispatch,
  errorMessage = '',
  success = true,
}) {
  const useCustomerAPIV1 = yield useCustomerV1T176413ForSagas();

  try {
    if (success) {
      if (accessToken) {
        yield call(saveData, 'token', accessToken);

        yield call(createLogAuth, {
          mixpanelKey: MIXPANEL_LOGIN_EVENTS.getTerms,
          useCustomerAPIV1,
        });

        const termOfService = yield call(getUserTermsOfService);

        if (termOfService?.success) {
          yield call(createLogAuth, {
            mixpanelKey: MIXPANEL_LOGIN_EVENTS.getTermsSuccess,
          });
        }

        yield call(createLogAuth, {
          mixpanelKey: MIXPANEL_LOGIN_EVENTS.getPersonalInfo,
          useCustomerAPIV1,
        });

        const personalInfo = yield call(getUserInformation, dispatch);

        if (personalInfo?.success) {
          yield call(createLogAuth, {
            mixpanelKey: MIXPANEL_LOGIN_EVENTS.getPersonalInfoSuccess,
            useCustomerAPIV1,
          });

          yield call(doPetLoad, dispatch);
        } else {
          throw new Error();
        }

        const payload = { accessToken };

        yield put(logIn(payload));
      } else {
        yield put(sessionError(errorMessage));
      }
    } else {
      yield put(sessionError('An error has ocurred'));
    }
  } catch (error) {
    if (error?.message) {
      yield call(createLogAuth, {
        dataDogTag: 'handleB2CToken js',
        error: error.message,
        httpStatus: 1,
        mixpanelKey: MIXPANEL_LOGIN_EVENTS.error,
        useCustomerAPIV1,
      });
    }

    yield put(sessionError('An error has ocurred'));
  }
}

export function* doLogin(action) {
  const { payload: { b2cToken, dispatch, email, isImpersonate } } = action;
  const useCustomerAPIV1 = yield useCustomerV1T176413ForSagas();

  yield call(saveData, 'token', b2cToken);

  yield call(createLogAuth, {
    mixpanelKey: MIXPANEL_LOGIN_EVENTS.registerB2C,
    useCustomerAPIV1,
  });

  if (isImpersonate) {
    yield call(saveData, IMPERSONATE_KEY, true);
    yield put(saveImpersonateCustomerId({
      adminEmail: email,
      customerId: '',
    }));
  }

  yield call(handleB2CToken, { accessToken: b2cToken, dispatch });
}

export function* doBannerVisibleChanged(_, action) {
  const { payload: { visible } } = action;
  const bannerPreferences = {
    visible,
  };
  yield call(
    saveData,
    BANNER_PREFERENCES_KEY,
    JSON.stringify(bannerPreferences),
  );
}

function* doLoadMultiModalData() {
  const store = yield select(({ policies, session }) => ({
    policies,
    session,
  }));
  const { CustomerId } = store.session.userInfo;
  const policy = store.policies?.allPolicies?.find((item) => item.Policy.Status
    === POLICY_STATUS.active);

  if (policy) {
    yield put(loadMultiModalData({ CustomerId }));
  }
}

export function* getTemporaryToken(dispatch) {
  yield put(attemptTemporaryToken());
  const response = yield call(
    get,
    dispatch,
    'api/Authentication/TemporaryToken',
  );

  const { IsValid, Data } = response;

  if (IsValid && Data) {
    yield put(setTemporaryToken({ publicToken: Data }));
    yield call(saveData, 'public_token', Data);
    yield put(temporaryTokenCompleted());
  } else {
    yield put(temporaryTokenFailed());
    yield put(showGenericError(true));
  }
}

export function* loadDataPrivateToken(dispatch) {
  yield call(getUserInformation, dispatch);
  yield call(getPolicySummary, dispatch);
  yield call(doLoadMultiModalData, dispatch);
  yield put(privateTokenCompleted());
  yield put(getAllBrandsData());
}

export function* getPrivateToken(dispatch, { payload }) {
  const { publicToken } = payload;
  yield put(attemptPrivateToken());
  const useCustomerApiV1 = yield usePetCloudCustomerV1T174565ForSagas();
  const urlService = useCustomerApiV1
    ? 'customer/Account'
    : 'api/Authentication';

  const response = yield call(
    apiCallWithApiKey,
    `${urlService}/TemporaryToken/Validate/${publicToken}`,
    {},
    dispatch,
  );

  const { Data, IsValid } = response;

  if (IsValid && Data) {
    const TOKEN_LEGACY = 'legacy';
    const CUSTOMER_LEGACY = 'customer';
    const PROXY_LEGACY = 'proxy';

    yield put(setPrivateToken({ privateToken: Data }));

    Data.forEach((tokenScope) => {
      if (tokenScope.Scope === PROXY_LEGACY) {
        saveData(WEBVIEW_API_TOKEN, tokenScope.Token);
        saveDataOnSession(WEBVIEW_API_TOKEN, tokenScope.Token);
      } else if (
        tokenScope.Scope === TOKEN_LEGACY
        || tokenScope.Scope === CUSTOMER_LEGACY
      ) {
        saveData(API_TOKEN, tokenScope.Token);
        // TODO: android inner webview no working localstorage
        saveDataOnSession(API_TOKEN, tokenScope.Token);
      }
    });

    yield call(delay, 100);
    yield put(loadDataForPrivateToken());
  } else {
    yield put(privateTokenFailed());
    yield put(showGenericError(true));
  }
}

export function* convertCustomerId(dispatch, action) {
  const customerId = action.payload;
  const response = yield call(
    apiCallWithApiKey,
    `api/Customer/CustomerIdDecrypter/${customerId}`,
    { method: 'POST' },
    dispatch,
  );

  const { Data, IsValid } = response;

  if (IsValid && Data) {
    yield put(saveImpersonateCustomerId({
      adminEmail: '',
      customerId: Data,
    }));
  } else {
    yield put(showGenericError(true));
  }
}

export function* acceptTermsOfService(dispatch) {
  const store = yield select(({ session }) => ({ session }));
  const { termsOfServiceVersionId: TermsVersionId } = store.session;
  const response = yield call(
    post,
    dispatch,
    'api/User/AcceptTermOfService',
    { TermsVersionId },
  );
  const { IsValid, Data } = response;

  if (IsValid && Data) {
    yield put(termsOfServiceAccepted());
  } else {
    yield put(showGenericError(true));
  }
}

export function* loadCustomerInformation(dispatch, { payload }) {
  const { marketingChannelId } = payload;

  const url = 'customer/Customer/CustomerMarketChannelDetail/'
    + `${marketingChannelId}`;
  const response = yield call(get, dispatch, url);

  if (response.IsValid) {
    yield put(customerInformationLoaded({
      addressPart1: response.Data?.AddressPart1,
      addressPart2: response.Data?.AddressPart2,
      displayName: response.Data?.DisplayName,
      email: response.Data?.Email,
      fullName: response.Data?.FullName,
      id: response.Data?.Id,
      originId: response.Data?.OriginId,
      phoneNumber1: response.Data?.PhoneNumber1,
      phoneNumber2: response.Data?.PhoneNumber2,
    }));
  } else {
    yield put(customerInformationFailed());
  }
}

export function* sessionSaga(store) {
  yield all([
    takeLatest(GET_DISCOUNT_INFO, getDiscountData, store.dispatch),
  ]);
}
